import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { useActivityFeed } from '@/hooks/useAdminMonitoring';
import { formatRelativeTime } from '@/utils/formatters';
import { cn } from '@/utils/cn';
import { getActivityCancellation, getCancellationActivityLabel } from '@/features/automations/utils/automation-execution-presentation';
import { Activity, UserPlus, AlertTriangle, CheckCircle, XCircle, Cpu, CreditCard, Shield, MinusCircle, type LucideIcon } from 'lucide-react';

const iconMap: Record<string, { icon: LucideIcon; color: string }> = {
  user_registered: { icon: UserPlus, color: 'text-success-400' },
  automation_completed: { icon: CheckCircle, color: 'text-success-400' },
  automation_failed: { icon: XCircle, color: 'text-danger-400' },
  automation_cancelled: { icon: MinusCircle, color: 'text-warning-500' },
  automation_started: { icon: Cpu, color: 'text-primary-400' },
  alert: { icon: AlertTriangle, color: 'text-warning-400' },
  payment: { icon: CreditCard, color: 'text-primary-400' },
  security: { icon: Shield, color: 'text-danger-400' },
  organisation_created: { icon: Activity, color: 'text-primary-400' },
};

const defaultIcon = { icon: Activity, color: 'text-neutral-400' };

export function RecentActivityFeed() {
  const { data, isLoading } = useActivityFeed();
  const activities = data?.activities || [];

  return (
    <Card>
      <CardHeader title="Activité récente" icon={<Activity className="h-4 w-4" />} />
      <CardContent className="p-0">
        {!isLoading && activities.length === 0 ? (
          <div className="px-6 py-4">
            <EmptyState
              message="Aucune activité récente"
              icon={<Activity className="h-8 w-8" />}
            />
          </div>
        ) : (
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800/50">
            {activities.map((activity, index) => {
              const { icon: Icon, color } = iconMap[activity.type] ?? defaultIcon;
              const cancellation = getActivityCancellation(activity);
              const cancellationLabel = getCancellationActivityLabel(activity);
              return (
                <div key={`${activity.type}-${activity.timestamp}-${index}`} className="flex items-start gap-3 px-6 py-3.5 transition-colors duration-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/30">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-neutral-100 dark:bg-neutral-800">
                    <Icon className={cn('h-3.5 w-3.5', color)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-neutral-700 truncate dark:text-neutral-300">{activity.message}</p>
                    {cancellationLabel && (
                      <p
                        className="mt-0.5 text-xs font-medium text-warning-700 dark:text-warning-400"
                        role={!cancellation || cancellation.requiresAttention ? 'alert' : 'status'}
                      >
                        {cancellationLabel}
                      </p>
                    )}
                    <p className="mt-0.5 text-xs text-neutral-500">{formatRelativeTime(activity.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
